import { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  InputAdornment,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useTranslation } from 'react-i18next';
import { COMPANY_STORAGE_KEY } from '../Keycloak';

interface CompanyPageProps {
  onCompanySet: (company: string) => void;
}

const BictorysLogo = () => (
  <svg width="44" height="38" viewBox="0 0 22 19" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11.7683 19C9.69936 19 7.63689 18.3657 5.9098 17.1063C5.11916 16.5285 4.42897 15.8377 3.86029 15.0505C3.50709 14.563 3.63994 13.8966 4.15678 13.5634C4.67361 13.2302 5.38 13.3555 5.7332 13.8431C6.16902 14.4438 6.69395 14.9726 7.29827 15.4128C9.81277 17.2469 13.267 17.3432 15.8949 15.6512C16.4117 15.318 17.1181 15.4433 17.4713 15.9309C17.8245 16.4185 17.6916 17.0849 17.1748 17.4181C15.5352 18.4742 13.6509 18.9984 11.7699 18.9984L11.7683 19Z" fill="#F0BD39"/>
    <path d="M5.93414 18.0371C5.69111 18.0371 5.44485 17.9637 5.23747 17.8109C1.94853 15.3945 0.694525 11.2907 2.11703 7.59648C2.45888 6.71 2.94655 5.87854 3.56546 5.12808C3.95106 4.66191 4.66231 4.57785 5.15646 4.94162C5.65061 5.30538 5.73971 5.97636 5.35411 6.44252C4.88103 7.01721 4.50839 7.64997 4.24754 8.32706C3.16203 11.1455 4.11955 14.2787 6.63081 16.122C7.12496 16.4858 7.21407 17.1567 6.82847 17.6229C6.60488 17.8934 6.27113 18.0356 5.93251 18.0356L5.93414 18.0371Z" fill="#E73A6E"/>
    <path d="M2.12519 8.62969C1.99233 8.62969 1.85786 8.60829 1.72663 8.56091C1.14013 8.35305 0.842018 7.73709 1.06236 7.1838C2.52537 3.50184 6.30035 1.14655 10.4642 1.32538C11.4638 1.36817 12.4489 1.55922 13.3934 1.89242C13.9799 2.10028 14.278 2.71624 14.0577 3.26952C13.8374 3.82281 13.1844 4.10404 12.5979 3.89618C11.8753 3.64093 11.1236 3.49573 10.3605 3.4621C7.18983 3.32608 4.30107 5.1235 3.18478 7.93426C3.01466 8.36374 2.58208 8.62969 2.12195 8.62969H2.12519Z" fill="#53C1E5"/>
    <path d="M18.4629 10.7083C18.4337 10.7083 18.403 10.7083 18.3722 10.7053C17.7484 10.6594 17.2818 10.1443 17.3304 9.55437C17.3904 8.82837 17.3304 8.10543 17.1538 7.40694C16.415 4.49072 13.7871 2.37233 10.6148 2.13695C9.99106 2.0911 9.52445 1.57602 9.57306 0.986054C9.62166 0.397613 10.1709 -0.0425719 10.793 0.00328064C14.9488 0.312021 18.3916 3.0861 19.3605 6.90868C19.5938 7.82573 19.6715 8.77335 19.5922 9.7225C19.5452 10.2834 19.0494 10.7083 18.4629 10.7083Z" fill="#46B38B"/>
    <path d="M13.4889 17.7467C12.9817 17.7467 12.52 17.4227 12.3904 16.9382C12.2365 16.365 12.6059 15.7842 13.2134 15.639C13.9619 15.4602 14.6683 15.1774 15.3148 14.7953C18.0075 13.2012 19.2939 10.1749 18.5162 7.26482C18.3639 6.69166 18.7317 6.11086 19.3393 5.96566C19.9468 5.82199 20.5625 6.16894 20.7164 6.7421C21.7355 10.5555 20.0489 14.5202 16.5202 16.608C15.6728 17.1094 14.7477 17.4823 13.7691 17.7146C13.6768 17.7375 13.5828 17.7467 13.4905 17.7467H13.4889Z" fill="#0052A3"/>
  </svg>
);

const CompanyPage = ({ onCompanySet }: CompanyPageProps) => {
  const { t } = useTranslation();
  const [company, setCompany] = useState('bictorys');
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = company.trim().toLowerCase();
    if (!trimmed) {
      setError(t('companyPage.emptyError'));
      return;
    }
    sessionStorage.setItem(COMPANY_STORAGE_KEY, trimmed);
    onCompanySet(trimmed);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(145deg, #0d1826 0%, #16243D 45%, #1f2e4a 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Decorative glow — top right */}
      <Box
        sx={{
          position: 'absolute',
          width: 600,
          height: 600,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(231,58,110,0.18) 0%, transparent 65%)',
          top: -180,
          right: -180,
          pointerEvents: 'none',
        }}
      />
      {/* Decorative glow — bottom left */}
      <Box
        sx={{
          position: 'absolute',
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(83,193,229,0.10) 0%, transparent 65%)',
          bottom: -150,
          left: -150,
          pointerEvents: 'none',
        }}
      />

      <Paper
        elevation={24}
        component="form"
        onSubmit={handleSubmit}
        sx={{
          px: { xs: 4, sm: 6 },
          py: { xs: 5, sm: 7 },
          width: { xs: '88%', sm: 460 },
          maxWidth: 500,
          borderRadius: 4,
          background: 'rgba(255,255,255,0.97)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 3,
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* Brand header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <BictorysLogo />
          <Typography
            variant="h5"
            fontWeight={800}
            sx={{ color: '#16243D', letterSpacing: '-0.5px' }}
          >
            Bictorys
          </Typography>
        </Box>

        <Box sx={{ width: 48, height: 3, bgcolor: '#E73A6E', borderRadius: 2 }} />

        {/* Welcome text */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" fontWeight={700} color="#16243D" gutterBottom>
            {t('companyPage.welcomeTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" lineHeight={1.6}>
            {t('companyPage.welcomeSubtitle')}
          </Typography>
        </Box>

        {/* Company input */}
        <TextField
          fullWidth
          label={t('companyPage.domainLabel')}
          placeholder={t('companyPage.domainPlaceholder')}
          value={company}
          onChange={(e) => {
            setCompany(e.target.value);
            if (error) setError('');
          }}
          error={!!error}
          helperText={error}
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <BusinessIcon sx={{ color: '#E73A6E' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused fieldset': { borderColor: '#E73A6E' },
            },
            '& .MuiInputLabel-root.Mui-focused': { color: '#E73A6E' },
          }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          endIcon={<ArrowForwardIcon />}
          sx={{
            bgcolor: '#E73A6E',
            '&:hover': { bgcolor: '#c52d5a' },
            py: 1.5,
            fontWeight: 700,
            fontSize: '1rem',
            borderRadius: 2,
          }}
        >
          {t('companyPage.submitButton')}
        </Button>
      </Paper>
    </Box>
  );
};

export default CompanyPage;
